import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import React from "react";
import { DocTypeMapping } from "../../models/ClientConfiguration/DocTypeMapping";
import AddableList from "../common/AddableList";
import FieldError from "../common/FieldError";

interface DocTypeMappingControlProps {
  docTypeMappings: DocTypeMapping[];
  onChange: (docTypeMappings: DocTypeMapping[]) => void;
  disabled?: boolean;
}

const DocTypeMappingsControl: React.FC<DocTypeMappingControlProps> = ({
  docTypeMappings,
  onChange,
  disabled = false
}) => {
  const handleAddDocTypeMapping = () => {
    const newDocTypeMapping: DocTypeMapping = {
      mapFrom: "",
      mapTo: "",
    };
    const updatedDocTypeMappings = [...docTypeMappings, newDocTypeMapping];
    onChange(updatedDocTypeMappings);
  };

  const handleDeleteDocTypeMapping = (index: number) => {
    const updatedDocTypeMappings = [...docTypeMappings];
    updatedDocTypeMappings.splice(index, 1);
    onChange(updatedDocTypeMappings);
  };

  const handleDocTypeMappingChange = (
    index: number,
    field: keyof DocTypeMapping,
    value: string,
  ) => {
    const updatedDocTypeMappings = [...docTypeMappings];
    updatedDocTypeMappings[index][field] = value;
    onChange(updatedDocTypeMappings);
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
    _: number,
  ) => {
    if (event.key === "Enter")
      event.preventDefault();
  };

  return (
    <div>
      <AddableList
        onAddClick={handleAddDocTypeMapping}
        subheaderText="DocType Mappings"
        disabled={disabled}
      >
        {docTypeMappings.map((docTypeMapping, index) => (
          <div key={index} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <div className="grid grid-cols-12 gap-2 w-full">
                <div className="col-span-6">
                  <Input
                    className="h-8"
                    placeholder="Map From"
                    value={docTypeMapping.mapFrom || ""}
                    onChange={(event) =>
                      handleDocTypeMappingChange(
                        index,
                        "mapFrom",
                        event.target.value,
                      )
                    }
                    onKeyDown={(event) =>
                      handleKeyPress(
                        event as React.KeyboardEvent<HTMLInputElement>,
                        index,
                      )
                    }
                    disabled={disabled}
                  />
                  <FieldError name={`docTypeMappings.${index}.mapFrom`} />
                </div>
                <div className="col-span-6">
                  <Input
                    className="h-8"
                    placeholder="Map To"
                    value={docTypeMapping.mapTo || ""}
                    onChange={(event) =>
                      handleDocTypeMappingChange(
                        index,
                        "mapTo",
                        event.target.value,
                      )
                    }
                    onKeyDown={(event) =>
                      handleKeyPress(
                        event as React.KeyboardEvent<HTMLInputElement>,
                        index,
                      )
                    }
                    disabled={disabled}
                  />
                  <FieldError name={`docTypeMappings.${index}.mapTo`} />
                </div>
              </div>
              <Button
                aria-label="Delete"
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => handleDeleteDocTypeMapping(index)}
                disabled={disabled}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
        ))}
      </AddableList>
    </div>
  );
};

export default DocTypeMappingsControl;
